import { getApiErrorMessage } from '@moko/utils';
import type {
  IPricingParameter,
  IPricingParameterBody,
  IPricingParameterDefinition,
  IResolvedPricingParameter,
} from '../../services/pricingParameterService';
import {
  formatDateOnlyForDisplay,
  fromApiDateOnly,
  toApiDateOnly,
} from '../../utils/dateOnly';
import { conditionId } from './pricingRateBody';
import { readApiProblem } from './tenantPricingOverrideHelpers';

export type ParameterSource = 'Global' | 'Tenant';

export interface ParameterFormValues {
  key: string;
  value: string;
  effectiveFrom: string;
  effectiveTo: string;
}

export interface ParameterRecordRow {
  id: number;
  source: ParameterSource;
  record: IPricingParameter;
}

export interface ParameterEffectiveRow {
  id: number;
  definition: IPricingParameterDefinition;
  record?: IResolvedPricingParameter;
}

export type ParsedParameterValue =
  | { ok: true; value: number }
  | { ok: false; error: string };

export const todayUtc = (now = new Date()): string =>
  now.toISOString().slice(0, 10);

export const parameterLabel = (key: string): string => {
  const spaced = key.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

export const formatParameterValue = (
  definition: IPricingParameterDefinition,
  value: number
): string =>
  `${value.toLocaleString('en', {
    maximumFractionDigits: definition.decimalPlaces,
  })} ${value === 1 ? definition.unit : `${definition.unit}s`}`;

export const formatRecordValue = (
  definitions: IPricingParameterDefinition[],
  record: IPricingParameter
): string => {
  const definition = definitions.find((item) => item.key === record.key);
  return definition
    ? formatParameterValue(definition, record.value)
    : String(record.value);
};

export const parseParameterValue = (
  definition: IPricingParameterDefinition,
  text: string
): ParsedParameterValue => {
  const trimmed = text.trim();
  if (!trimmed) {
    return { ok: false, error: 'Value is required. Enter 0 for zero.' };
  }
  const places = definition.decimalPlaces;
  const pattern =
    places === 0 ? /^\d+$/ : new RegExp(`^\\d+(\\.\\d{1,${places}})?$`);
  if (!pattern.test(trimmed)) {
    return {
      ok: false,
      error:
        places === 0
          ? 'Enter a whole number.'
          : `Enter a number with at most ${places} decimal places.`,
    };
  }
  const value = Number(trimmed);
  if (value < definition.minimum || value > definition.maximum) {
    return {
      ok: false,
      error: `Value must be between ${definition.minimum.toLocaleString(
        'en'
      )} and ${definition.maximum.toLocaleString('en')}.`,
    };
  }
  return { ok: true, value };
};

export const validateParameterValue =
  (definition: IPricingParameterDefinition) =>
  (text: string): string | true => {
    const parsed = parseParameterValue(definition, text);
    return parsed.ok || parsed.error;
  };

export const inheritanceLabel = (
  record?: IResolvedPricingParameter
): string => {
  if (!record) return 'Not set';
  if (record.source === 'Global') return 'Inherited';
  const end = formatDateOnlyForDisplay(record.effectiveTo);
  return end ? `Overridden until ${end}` : 'Overridden';
};

export interface ParameterQuery {
  pricingItemId: string;
  pricingDate: string;
}

export const emptyParameterQuery = (): ParameterQuery => ({
  pricingItemId: '',
  pricingDate: todayUtc(),
});

export const queryPricingItemId = (query: ParameterQuery): number | undefined =>
  conditionId(query.pricingItemId) ?? undefined;

export const validateEffectiveFrom = (value: string): string | true =>
  !!fromApiDateOnly(value) || 'Effective from is required.';

export const validateEffectiveTo = (
  value: string,
  effectiveFrom: string
): string | true => {
  const to = fromApiDateOnly(value);
  const from = fromApiDateOnly(effectiveFrom);
  return to && from && to < from
    ? 'Effective to must be on or after effective from.'
    : true;
};

export const initialFormValues = (
  record?: IPricingParameter,
  today = todayUtc()
): ParameterFormValues =>
  record
    ? {
        key: record.key,
        value: String(record.value),
        effectiveFrom: fromApiDateOnly(record.effectiveFrom),
        effectiveTo: fromApiDateOnly(record.effectiveTo),
      }
    : { key: '', value: '', effectiveFrom: today, effectiveTo: '' };

export const toParameterBody = (
  pricingItemId: number,
  values: ParameterFormValues,
  value: number
): IPricingParameterBody => ({
  pricingItemId,
  key: values.key,
  value,
  effectiveFrom: toApiDateOnly(values.effectiveFrom),
  effectiveTo: toApiDateOnly(values.effectiveTo) || null,
});

export const toRecordRows = (
  groups: [ParameterSource, IPricingParameter[]][]
): ParameterRecordRow[] =>
  groups
    .flatMap(([source, records]) =>
      records.map((record) => ({ source, record }))
    )
    .sort(
      (left, right) =>
        left.record.key.localeCompare(right.record.key) ||
        fromApiDateOnly(right.record.effectiveFrom).localeCompare(
          fromApiDateOnly(left.record.effectiveFrom)
        ) ||
        left.source.localeCompare(right.source)
    )
    .map((row, index) => ({ ...row, id: index + 1 }));

export const toEffectiveRows = (
  definitions: IPricingParameterDefinition[],
  resolved: IResolvedPricingParameter[]
): ParameterEffectiveRow[] =>
  definitions.map((definition, index) => ({
    id: index + 1,
    definition,
    record: resolved.find((record) => record.key === definition.key),
  }));

export const getParameterErrorMessage = (
  error: unknown,
  fallback: string
): string => {
  const { status, fieldErrors } = readApiProblem(error);
  if (fieldErrors.length) return fieldErrors.join(' ');
  const detail = getApiErrorMessage(error, fallback);
  if (status === 409) {
    return `Conflict: ${detail}`;
  }
  if (status === 404) {
    return `Not found: ${detail}. The value may have been deleted. Refresh and try again.`;
  }
  if (status === 403) {
    return "You don't have permission to change pricing parameters.";
  }
  if (status === 400) return `Invalid value: ${detail}`;
  return detail;
};
